import { useMemo, useState } from 'react';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { toSdks } from '@gonfalon/navigator/navigators';
import type { SdkVersionDetailsRep } from '@gonfalon/openapi/types';
import { useParam } from '@gonfalon/router/useParam';
import {
  Button,
  Cell,
  Column,
  Heading,
  Input,
  Link,
  LinkButton,
  Row,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TagGroup,
  TagList,
  Text,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import type { SDKConnection } from './types';
import { getStatusTagVariant, mapEolStatusToStatus, SDKStatus } from './types';
import { useSdkVersionsDetails } from './useSdkVersionsDetails';

import styles from './SDKApplicationDetail.module.css';

type SDKApplicationDetailProps = {
  applicationId?: string;
  onBackClick?: () => void;
};

export function SDKApplicationDetail({
  applicationId: applicationIdProp,
  onBackClick,
}: SDKApplicationDetailProps = {}) {
  const routeApplicationId = useParam('applicationId', { optional: true });
  const applicationId = applicationIdProp ?? routeApplicationId;
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch SDK version details from the API
  const { data: apiResponse } = useSdkVersionsDetails();

  // Transform API data to SDK connections for this application
  const sdkConnections = useMemo((): SDKConnection[] => {
    if (!apiResponse || !applicationId) {
      return [];
    }

    // Filter SDKs for this application and transform to display format
    return apiResponse
      .filter((sdk: SdkVersionDetailsRep) => (sdk.applicationId || 'unknown') === applicationId)
      .map(
        (sdk: SdkVersionDetailsRep, index: number): SDKConnection => ({
          id: `${sdk.projectId}-${sdk.environmentId}-${sdk.name}-${index}`,
          projectId: sdk.projectName || sdk.projectKey || sdk.projectId || '',
          environmentId: sdk.environmentName || sdk.environmentKey || sdk.environmentId || '',
          sdkName: sdk.name || 'Unknown',
          sdkType: sdk.type || '',
          sdkVersion: sdk.version || '',
          connectionType: sdk.connectionType || '',
          relayVersion: sdk.relayVersion || '',
          status: mapEolStatusToStatus(sdk.eolStatus),
          eolStatus: sdk.eolStatus,
          latestVersion: sdk.ldLatestVersion,
          latestReleaseUrl: sdk.latestReleaseUrl,
        }),
      );
  }, [apiResponse, applicationId]);

  const applicationName = applicationId || msg('billing.sdkApplication.unknownApplication');

  const needsAttentionSDKs = useMemo(() => {
    const filtered = sdkConnections.filter(
      (sdk) => sdk.status === SDKStatus.EOL_APPROACHING || sdk.status === SDKStatus.OUT_OF_DATE,
    );
    // Sort: "No longer supported" first, then "End of Life Approaching"
    return filtered.sort((a, b) => {
      if (a.status === SDKStatus.OUT_OF_DATE && b.status === SDKStatus.EOL_APPROACHING) {
        return -1;
      }
      if (a.status === SDKStatus.EOL_APPROACHING && b.status === SDKStatus.OUT_OF_DATE) {
        return 1;
      }
      return 0;
    });
  }, [sdkConnections]);

  const filteredSDKs = useMemo(() => {
    if (!searchQuery) {
      return sdkConnections;
    }

    const query = searchQuery.toLowerCase();
    return sdkConnections.filter(
      (sdk) =>
        sdk.projectId.toLowerCase().includes(query) ||
        sdk.environmentId.toLowerCase().includes(query) ||
        sdk.sdkName.toLowerCase().includes(query) ||
        sdk.sdkType.toLowerCase().includes(query) ||
        sdk.sdkVersion.toLowerCase().includes(query) ||
        sdk.connectionType.toLowerCase().includes(query) ||
        sdk.relayVersion.toLowerCase().includes(query) ||
        sdk.status.toLowerCase().includes(query),
    );
  }, [sdkConnections, searchQuery]);

  return (
    <div className={styles.container}>
      <div>
        {onBackClick ? (
          <Button variant="minimal" onPress={onBackClick}>
            <Icon name="arrow-left-thin" size="small" slot="start-icon" />{' '}
            {msg('billing.sdkApplication.backToApplications')}
          </Button>
        ) : (
          <LinkButton variant="minimal" href={toSdks()}>
            <Icon name="arrow-left-thin" size="small" slot="start-icon" />{' '}
            {msg('billing.sdkApplication.backToApplications')}
          </LinkButton>
        )}
        <Heading size="large" className={styles.heading}>
          {applicationName}
        </Heading>
      </div>

      {needsAttentionSDKs.length > 0 && (
        <div className={styles.attentionSection} data-test-id="sdks-needing-attention">
          <Heading size="medium" className={styles.attentionTitle}>
            <Message id="billing.sdkApplication.needsAttentionHeading" values={{ count: needsAttentionSDKs.length }} />
          </Heading>
          <div className={styles.attentionGrid}>
            {needsAttentionSDKs.map((sdk) => (
              <div key={sdk.id} className={styles.attentionCard} data-test-id={`attention-card-${sdk.id}`}>
                <div className={styles.attentionCardHeader}>
                  <span className={styles.attentionCardTitle}>{sdk.sdkName}</span>
                  <TagGroup
                    aria-label={msg('billing.sdkApplication.statusLabel')}
                    className={styles.attentionStatusTagGroup}
                  >
                    <TagList>
                      <Tag
                        variant={getStatusTagVariant(sdk.status)}
                        data-test-id={`attention-status-${sdk.id}`}
                        className={styles.attentionStatusTag}
                      >
                        {sdk.status}
                      </Tag>
                    </TagList>
                  </TagGroup>
                </div>
                <div className={styles.attentionCardBody}>
                  <div className={styles.attentionCardRow}>
                    <span className={styles.attentionCardLabel}>
                      <Message id="billing.sdkApplication.project" />
                    </span>
                    <span>{sdk.projectId}</span>
                  </div>
                  <div className={styles.attentionCardRow}>
                    <span className={styles.attentionCardLabel}>
                      <Message id="billing.sdkApplication.environment" />
                    </span>
                    <span>{sdk.environmentId}</span>
                  </div>
                  <div className={styles.attentionCardRow}>
                    <span className={styles.attentionCardLabel}>
                      <Message id="billing.sdkApplication.version" />
                    </span>
                    <span>{sdk.sdkVersion}</span>
                  </div>
                  {sdk.latestVersion && (
                    <div className={styles.attentionCardRow}>
                      <span className={styles.attentionCardLabel}>
                        <Message id="billing.sdkApplication.latest" />
                      </span>
                      <span>{sdk.latestVersion}</span>
                    </div>
                  )}
                  <div className={styles.attentionCardFooter}>
                    <Link
                      href={sdk.latestReleaseUrl || 'https://launchdarkly.com/docs/sdk'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.updateLink}
                      data-test-id="sdk-update-link"
                    >
                      {sdk.latestReleaseUrl
                        ? msg('billing.sdkApplication.viewLatestRelease')
                        : msg('billing.sdkApplication.viewDocs')}
                      <Icon name="link-external" size="small" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={styles.tableSection}>
        <div className={styles.tableHeader}>
          <Heading size="medium" className={styles.heading}>
            <Message id="billing.sdkApplication.allConnectionsHeading" values={{ count: sdkConnections.length }} />
          </Heading>
          <Input
            type="search"
            placeholder={msg('billing.sdkApplication.filterPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchInput}
            data-test-id="sdk-connections-search-input"
          />
        </div>

        {filteredSDKs.length === 0 ? (
          <Text>
            <Message id="billing.sdkApplication.noSdkConnections" />
          </Text>
        ) : (
          <div className={styles.tableWrapper}>
            <Table
              aria-label={msg('billing.sdkApplication.tableLabel')}
              className={styles.table}
              data-test-id="sdk-connections-table"
            >
              <TableHeader
                columns={[
                  { id: 'project', name: msg('billing.sdkApplication.projectColumn'), isRowHeader: true },
                  { id: 'environment', name: msg('billing.sdkApplication.environmentColumn') },
                  { id: 'sdkName', name: msg('billing.sdkApplication.sdkNameColumn') },
                  { id: 'sdkType', name: msg('billing.sdkApplication.sdkTypeColumn') },
                  { id: 'version', name: msg('billing.sdkApplication.versionColumn') },
                  { id: 'connectionType', name: msg('billing.sdkApplication.connectionTypeColumn') },
                  { id: 'relayVersion', name: msg('billing.sdkApplication.relayVersionColumn') },
                  { id: 'status', name: msg('billing.sdkApplication.statusColumn') },
                ]}
              >
                {(column) => <Column isRowHeader={column.isRowHeader}>{column.name}</Column>}
              </TableHeader>
              <TableBody>
                {filteredSDKs.map((sdk) => (
                  <Row key={sdk.id}>
                    <Cell>{sdk.projectId}</Cell>
                    <Cell>{sdk.environmentId}</Cell>
                    <Cell>{sdk.sdkName}</Cell>
                    <Cell>{sdk.sdkType}</Cell>
                    <Cell>{sdk.sdkVersion}</Cell>
                    <Cell>{sdk.connectionType}</Cell>
                    <Cell>{sdk.relayVersion || '—'}</Cell>
                    <Cell>
                      <TagGroup aria-label={msg('billing.sdkApplication.statusLabel')}>
                        <TagList>
                          <Tag variant={getStatusTagVariant(sdk.status)}>{sdk.status}</Tag>
                        </TagList>
                      </TagGroup>
                    </Cell>
                  </Row>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {filteredSDKs.length > 0 && (
          <Text size="small">
            <Message
              id="billing.sdkApplication.showingConnections"
              values={{ shown: filteredSDKs.length, total: sdkConnections.length }}
            />
          </Text>
        )}
      </div>
    </div>
  );
}
